import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Alert, AppBar, Box, Button, Chip, CircularProgress, Container, IconButton, Paper,
    Stack, ToggleButton, ToggleButtonGroup, Toolbar, Tooltip, Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import MarkEmailUnreadOutlinedIcon from '@mui/icons-material/MarkEmailUnreadOutlined';
import ReplyIcon from '@mui/icons-material/Reply';
import LogoutIcon from '@mui/icons-material/Logout';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import api from '../api/axios';

const dateFormat = new Intl.DateTimeFormat('es-GT', { dateStyle: 'medium', timeStyle: 'short' });

export default function Panel() {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [filter, setFilter] = useState('all');
    const navigate = useNavigate();

    const load = useCallback(async () => {
        setLoading(true); setError('');
        try {
            const { data } = await api.get('/messages/');
            setMessages(data);
        } catch {
            setError('No se pudieron cargar las solicitudes.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const unread = messages.filter((m) => !m.is_read).length;
    const visible = useMemo(
        () => (filter === 'unread' ? messages.filter((m) => !m.is_read) : messages),
        [messages, filter]
    );

    const toggleRead = async (m) => {
        const { data } = await api.patch(`/messages/${m.id}/`, { is_read: !m.is_read });
        setMessages((list) => list.map((x) => (x.id === m.id ? data : x)));
    };

    const remove = async (m) => {
        if (!window.confirm(`¿Eliminar la solicitud de ${m.name}?`)) return;
        await api.delete(`/messages/${m.id}/`);
        setMessages((list) => list.filter((x) => x.id !== m.id));
    };

    const logout = () => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        navigate('/login');
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            <AppBar position="sticky" elevation={0} sx={{ bgcolor: '#0b1220' }}>
                <Toolbar>
                    <Typography variant="h6" sx={{ flexGrow: 1 }}>Panel privado</Typography>
                    <Button color="inherit" href="/panel/estudio">Estudio</Button>
                    <Button color="inherit" href="/panel/importar">Importar</Button>
                    <Button color="inherit" href="/panel/gratitud">Gratitud</Button>
                    <Button color="inherit" href="/" sx={{ mr: 1 }}>Ver sitio</Button>
                    <Button color="inherit" startIcon={<LogoutIcon />} onClick={logout}>Salir</Button>
                </Toolbar>
            </AppBar>

            <Container maxWidth="md" sx={{ py: 5 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between"
                    alignItems={{ sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 800 }}>Solicitudes de contacto</Typography>
                        <Typography color="text.secondary">
                            {messages.length} en total · {unread} sin leer
                        </Typography>
                    </Box>
                    <ToggleButtonGroup size="small" exclusive value={filter} onChange={(_, v) => v && setFilter(v)}>
                        <ToggleButton value="all">Todas</ToggleButton>
                        <ToggleButton value="unread">Sin leer</ToggleButton>
                    </ToggleButtonGroup>
                </Stack>

                {error && <Alert severity="error" action={<Button onClick={load}>Reintentar</Button>}>{error}</Alert>}

                {loading ? (
                    <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}><CircularProgress /></Box>
                ) : visible.length === 0 && !error ? (
                    <Paper variant="outlined" sx={{ p: 6, textAlign: 'center' }}>
                        <InboxOutlinedIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                        <Typography color="text.secondary" sx={{ mt: 1 }}>
                            {filter === 'unread' ? 'No hay solicitudes sin leer.' : 'Aún no hay solicitudes.'}
                        </Typography>
                    </Paper>
                ) : (
                    <Stack spacing={2}>
                        {visible.map((m) => (
                            <Paper key={m.id} variant="outlined" sx={{
                                p: 3, borderLeft: 4, borderLeftColor: m.is_read ? 'divider' : 'primary.main',
                            }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                                            <Typography variant="h6" sx={{ fontSize: '1.05rem' }}>{m.subject}</Typography>
                                            {!m.is_read && <Chip label="Nueva" size="small" color="primary" />}
                                        </Stack>
                                        <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                                            {m.name} · {m.email}{m.phone && ` · ${m.phone}`}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {dateFormat.format(new Date(m.created_at))}
                                        </Typography>
                                    </Box>
                                    <Stack direction="row" sx={{ flexShrink: 0 }}>
                                        <Tooltip title="Responder por correo">
                                            <IconButton href={`mailto:${m.email}?subject=${encodeURIComponent('Re: ' + m.subject)}`}>
                                                <ReplyIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={m.is_read ? 'Marcar como no leída' : 'Marcar como leída'}>
                                            <IconButton onClick={() => toggleRead(m)}>
                                                {m.is_read ? <MarkEmailUnreadOutlinedIcon /> : <MarkEmailReadOutlinedIcon />}
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Eliminar">
                                            <IconButton onClick={() => remove(m)}><DeleteOutlineIcon /></IconButton>
                                        </Tooltip>
                                    </Stack>
                                </Stack>
                                <Typography sx={{ mt: 2, whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>{m.message}</Typography>
                            </Paper>
                        ))}
                    </Stack>
                )}
            </Container>
        </Box>
    );
}
